import styled from "@emotion/styled";
import { trackEvent } from "contexts/mixpanel";
import {
  Alert,
  AllCaps,
  Box,
  FlexRow,
  H3,
  IconClock,
  IconPalette,
  IconUsers,
  Link,
  NoBreak,
  Stack,
  Text,
} from "atoms";
import { formatDate } from "utils/date";
import type { OpenCallTag } from "../types";
import { getOpenCallTagColor } from "../utils/getOpenCallTagColor";
import { OpenCallDetailsButton } from "./OpenCallDetailsButton";
import { OpenCallDetailsHeading } from "./OpenCallDetailsHeading";

const trackVote = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Open Call Vote",
    ButtonContext: "Open Call Submission",
  });

const trackSignUpSubmit = () => () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign Up",
    ButtonContext: "Open Call Submission",
  });

const trackSignUpVote = () => () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign Up",
    ButtonContext: "Open Call Vote",
  });

const trackSignInSubmit = () => () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign In",
    ButtonContext: "Open Call Submission",
  });

const trackSignInVote = () => () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign In",
    ButtonContext: "Open Call Vote",
  });

const trackSubmit = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Open Call Submit",
    ButtonContext: "Open Call Submission",
  });

const trackBuySubmissions = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Buy Additional Submissions",
    ButtonContext: "Open Call Submission",
  });

/**
 * Details Card on Open Call Details page
 */
type OpenCallDetails = {
  allowPaidSubmissions: boolean;
  communityVotingEndDate?: string;
  communityVotingStartDate?: string;
  isCommunityCuration: boolean;
  isSelectionCompleted: boolean;
  isUser: boolean;
  isEligible: boolean;
  maxWinners: number;
  /* Number of submissions submitted by current user */
  numberSubmitted: number;
  openCallId: string;
  openCallTitle: string;
  refetchOpenCall: VoidFunction;
  showSubmissions: boolean;
  /* Total number of submissions by all users */
  submissionCount: number;
  submissionEndDate?: string;
  submissionStartDate: string;
  tag: OpenCallTag;
  userIsWinner: boolean;
  userSubmissionAllowance: number;
  canPurchaseSubmissions: boolean;
  usersVotes: string[];
  voted: boolean;
};

function OpenCallDetailsCard({
  allowPaidSubmissions,
  communityVotingEndDate,
  communityVotingStartDate,
  isCommunityCuration,
  isSelectionCompleted,
  isUser,
  isEligible,
  maxWinners,
  numberSubmitted,
  openCallId,
  openCallTitle,
  refetchOpenCall,
  showSubmissions,
  submissionCount,
  submissionEndDate,
  submissionStartDate,
  tag,
  userIsWinner,
  userSubmissionAllowance,
  canPurchaseSubmissions,
  usersVotes,
  voted,
}: OpenCallDetails) {
  /** number of submissions gained when they are purchased */
  const submissionsPerPurchase = 2;
  /** Sets number of free submissions */
  const freeSubmissions = 1;
  /** Sets number of paid submissions */
  const paidSubmissions = !canPurchaseSubmissions ? submissionsPerPurchase : 0;
  const maxSubmissions = allowPaidSubmissions
    ? freeSubmissions + submissionsPerPurchase
    : freeSubmissions;
  const remainingSubmissions = userSubmissionAllowance - numberSubmitted;
  const userHasSubmitted = numberSubmitted > 0;
  const tagColor = getOpenCallTagColor(tag);

  return (
    <DetailsCard tagColor={tagColor}>
      <Stack gap="1.25rem">
        <OpenCallDetailsHeading
          tag={tag}
          userVoted={voted}
          userIsWinner={userIsWinner}
          submissionEndDate={submissionEndDate}
          communityVotingStartDate={communityVotingStartDate}
          communityVotingEndDate={communityVotingEndDate}
          submissionStartDate={submissionStartDate}
          numberSubmitted={numberSubmitted}
          canPurchaseSubmissions={canPurchaseSubmissions}
          refetchOpenCall={refetchOpenCall}
          remainingSubmissions={remainingSubmissions}
          isEligible={isEligible}
        />

        {isUser && userHasSubmitted && !isEligible && (
          <SubmissionIneligible
            tag={tag}
            hasVoting={isCommunityCuration}
            numberSubmitted={numberSubmitted}
          />
        )}

        <OpenCallDetailsButton
          isUser={isUser}
          numberSubmitted={numberSubmitted}
          openCallId={openCallId}
          openCallTitle={openCallTitle}
          paidSubmissions={paidSubmissions}
          tag={tag}
          trackBuySubmissions={trackBuySubmissions}
          // trackSubmitEdit={noop}
          trackSignUpVote={trackSignUpVote}
          trackSignUpSubmit={trackSignUpSubmit}
          trackSignInVote={trackSignInVote}
          trackSignInSubmit={trackSignInSubmit}
          trackSubmit={trackSubmit}
          trackVote={trackVote}
          userSubmissionAllowance={userSubmissionAllowance}
          usersVotes={usersVotes}
          maxSubmissions={maxSubmissions}
          canPurchaseSubmissions={canPurchaseSubmissions}
          // isEligible={isEligible}
        />

        <OpenCallMeta gap="xs">
          <DetailsDate
            tag={tag}
            submissionStartDate={submissionStartDate}
            submissionEndDate={submissionEndDate}
            communityVotingStartDate={communityVotingStartDate}
            communityVotingEndDate={communityVotingEndDate}
          />

          {!submissionEndDate && (
            <FlexRow alignItems="flex-start" gap="7px" flexWrap="nowrap">
              <IconClock />
              <span style={{ flex: "1 1 auto" }}>
                Submissions are ongoing and will be
                reviewed&nbsp;on&nbsp;a&nbsp;rolling&nbsp;basis.
              </span>
            </FlexRow>
          )}

          <FlexRow gap=".5rem 1.25rem">
            {!!maxWinners && (
              <FlexRow alignItems="flex-start" gap="7px" flexWrap="nowrap">
                <IconPalette />
                <PriceAndQty size="xs">
                  {maxWinners}{" "}
                  {isSelectionCompleted ? (
                    <>
                      artwork
                      {maxWinners === 1 ? " has" : "s have"} been selected
                    </>
                  ) : (
                    <>artwork{maxWinners === 1 ? "" : "s"} to be selected</>
                  )}
                </PriceAndQty>
              </FlexRow>
            )}

            <FlexRow alignItems="flex-start" gap="7px" flexWrap="nowrap">
              <IconUsers />
              <SubmissionCountText
                submissionCount={submissionCount}
                showSubmissions={showSubmissions}
                tag={tag}
              />
            </FlexRow>
          </FlexRow>
        </OpenCallMeta>
      </Stack>
    </DetailsCard>
  );
}

const DetailsCard = styled(Box)<{ tagColor?: string }>(
  ({ tagColor, theme }) => ({
    // marginTop: "-4rem",
    position: "relative",
    zIndex: 3,
    ...(tagColor && {
      borderTop: "8px solid",
      borderColor: theme.colors[tagColor],
    }),
    [theme.breakpoints.desktop]: {
      ...(tagColor && {
        borderTop: 0,
        borderLeft: "8px solid",
        borderColor: theme.colors[tagColor],
      }),
    },
    [theme.breakpoints.desktopMedium]: {
      maxWidth: 900,
    },
    [theme.breakpoints.desktopLarge]: {
      padding: "1.5rem",
    },
  })
);

const PriceAndQty = styled(Text)({
  alignItems: "center",
  display: "inline-flex",
  gap: 6,
  margin: 0,
});

const OpenCallMeta = styled(Stack)(({ theme }) => ({
  gridArea: "meta",
  fontSize: theme.fontSizes.xs,
  lineHeight: 1.2,
  svg: {
    alignSelf: "center",
  },
  "&+&": {
    padding: 0,
  },
  "&:empty": {
    display: "none",
  },
}));

function SubmissionIneligible({
  hasVoting,
  tag,
  numberSubmitted,
}: {
  hasVoting: boolean;
  tag: OpenCallTag;
  numberSubmitted: number;
}) {
  if (!["Submissions Open", "Voting Soon", "Voting Open"].includes(tag)) {
    return null;
  }

  return (
    <Alert type="error">
      <Stack>
        <H3 size="sm">
          Your submission{numberSubmitted === 1 ? " is" : "s are"} not yet
          eligible
        </H3>
        <Text size="xs">
          Your submission{numberSubmitted === 1 ? "" : "s"} will remain
          ineligible{" "}
          {hasVoting && "and will not be displayed during Community Voting "}
          until you have applied for a <AllCaps>Hug</AllCaps> Artist Profile.
        </Text>
        <Link to="/artist-application" size="xs" variant="primary" arrow="r">
          Apply for an artist profile
        </Link>
      </Stack>
    </Alert>
  );
}

type SubmissionCountTextProps = {
  submissionCount?: number;
  showSubmissions: boolean;
  tag: OpenCallTag;
};
function SubmissionCountText({
  submissionCount = 0,
  showSubmissions,
  tag,
}: SubmissionCountTextProps) {
  if (submissionCount === 0 && tag === "Submissions Open")
    return <Link to="submit">Be the first to submit!</Link>;
  if (submissionCount > 0 && showSubmissions)
    return <Link to="#submissions">{submissionCount} artworks submitted</Link>;
  return <>{submissionCount} artworks submitted</>;
}

function DetailsDate({
  tag,
  submissionEndDate,
  communityVotingStartDate,
  communityVotingEndDate,
  submissionStartDate,
}: {
  tag: OpenCallTag;
  submissionStartDate: string;
  submissionEndDate?: string;
  communityVotingStartDate?: string;
  communityVotingEndDate?: string;
}) {
  let end = "";
  let text = "";

  if (tag === undefined) return null;
  if (tag === "Coming Soon" && !!submissionStartDate) {
    text = "Submissions open on ";
    end = submissionStartDate;
  } else if (tag === "Submissions Open" && !!submissionEndDate) {
    text = "Submissions close on ";
    end = submissionEndDate;
  } else if (tag === "Voting Soon" && !!communityVotingStartDate) {
    text = "Voting opens on ";
    end = communityVotingStartDate;
  } else if (tag === "Voting Open" && !!communityVotingEndDate) {
    text = "Voting ends on ";
    end = communityVotingEndDate;
  }

  return !end ? null : (
    <FlexRow alignItems="flex-start" gap="7px" flexWrap="nowrap">
      <IconClock />
      <span>
        {text}{" "}
        <NoBreak>
          {formatDate(end, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            timeZone: "America/New_York",
            timeZoneName: "short",
          })}
        </NoBreak>
      </span>
    </FlexRow>
  );
}

export { OpenCallDetailsCard };
